import {Utils} from './Utils';
import {StringUtils} from './StringUtils';
import {FileUtils} from './FileUtils';
import {UriHelper} from './UriHelper';
import {ArgValidator} from './ArgValidator';

declare var Strings: any;

export interface IStringsMetadata {
    default: string;
    languages: Array<string>;
}

export type StringsLoadCompleteCallback = (lang: string) => void;

/**
 * Returns language key to be used to initialize strings factory with.
 */
export type StringsInitLoadCallback = () => {
    initialLanguage: string
};

export type StringsFactoryInitializeOptions = {
    loadComplete: StringsLoadCompleteCallback;
    initLoadCallback: StringsInitLoadCallback;
    versionId: string;
    path?: string;
}

// TODO comment whole class
export class StringsFactory {
    private static _metadata: IStringsMetadata;
    private static _initialized: boolean;
    private static _currentLang: string;
    private static _onLoadComplete: StringsLoadCompleteCallback;
    private static _initVersionId: string;

    public static get initialized() {
        return StringsFactory._initialized;
    }

    public static getDefaultLang(): string {
        return Utils.isNullOrUndefined(this._metadata) ? 'en' : this._metadata.default;
    }

    public static get currentLanguage(): string {
        StringsFactory.checkInitialized();
        return StringsFactory._currentLang;
    }

    public static set currentLanguage(value: string) {
        StringsFactory.checkInitialized();
        StringsFactory._currentLang = value;
    }

/*    public static getCurrentLang(): string {
        StringsFactory.checkInitialized();
        return StringsFactory._currentLang;
    }

    public static setCurrentLang(lang: string): void {
        StringsFactory._currentLang = lang;
    }*/

    public static getLanguages(): Array<string> {
        StringsFactory.checkInitialized();
        return StringsFactory._metadata.languages;
    }

    private static isFullLang(lng: string): boolean {
        return lng.indexOf('-') > -1;
    }

    public static checkInitialized() {
        if (!StringsFactory.initialized) {
            throw new Error('StringsFactory was not initialized, call StringsFactory.initialize() on startup!');
        }
    }

    private static loadComplete(): void {
        // simulate async
        setTimeout(() => {
            const callback = StringsFactory._onLoadComplete;
            if (callback) {
                callback(StringsFactory._currentLang);
            }
        }, 100);
    }

    public static change(lang: string): Promise<string> {
        return Promise.resolve()
            .then(() => {
                this.checkInitialized();
                return StringsFactory.internalLoad(lang, StringsFactory._initVersionId);
            });
    }

    public static initialize(options: StringsFactoryInitializeOptions): Promise<string> {
        return Promise.resolve()
            .then(() => {
                ArgValidator.ensureIsNotNullOrUndefined(options, 'options');
                ArgValidator.ensureIsNotNullOrUndefined(options.loadComplete, 'options.loadComplete');
                ArgValidator.ensureIsNotNullOrUndefined(options.initLoadCallback, 'options.initLoadCallback');

                StringsFactory._onLoadComplete = options.loadComplete;
                const initLoadData = options.initLoadCallback();
                let lang = Utils.isNullOrUndefined(initLoadData) ? null : Utils.valueOrDefault(initLoadData.initialLanguage, null);

                /*const query = Utils.getURIQuery();
                let lang = Utils.valueOrDefault(query['lang'], '');*/

                if (StringUtils.isNullOrEmpty(lang)) {
                    lang = StringsFactory.getDefaultLang();
                }

                StringsFactory._initVersionId = options.versionId;
                return StringsFactory.internalLoad(lang, options.versionId, options.path);
            });
    }

    private static internalLoad(lang: string, versionId: string, path?: string): Promise<string> {
        path = Utils.valueOrDefault(path, 'Resources/');

        function _buildLangFile(): string {
            return `${path}Strings.${lang}.json`;
        }

        const metadataFile = `${path}Strings-metadata.json`;
        const langFile = _buildLangFile();

        function _loadDefault(err: any) {
            console.log(`StringsFactory.load() failed for lang "${lang}", err: `, Utils.getErrorMsg(err));
            lang = StringsFactory.getDefaultLang();
            var file = _buildLangFile();
            return FileUtils.readLocalJsonFile(UriHelper.setTimeStamp(file, versionId))
                .then(json => {
                    new Strings(json);
                    StringsFactory._initialized = true;
                    StringsFactory.currentLanguage = lang;
                    return lang;
                });
        }

        function _loadNeutral(err: any, fullLang: string) {
            var idx = fullLang.indexOf('-'); // cs-cz
            var neutralLang = fullLang.substring(0, idx);

            // fix norwegian language from nb to no
            if(neutralLang === 'nb') {
                neutralLang = 'no';
            }
            lang = neutralLang;
            console.log(`StringsFactory.load() failed for lang "${fullLang}", trying to load neutral language '${neutralLang}', err: `,
                Utils.getErrorMsg(err));

            var file = _buildLangFile();
            return FileUtils.readLocalJsonFile(UriHelper.setTimeStamp(file, versionId))
                .then(json => {
                    new Strings(json);
                    StringsFactory._initialized = true;
                    StringsFactory.currentLanguage = lang;
                    return lang;
                });
        }

        function _load() {
            return FileUtils.readLocalJsonFile(UriHelper.setTimeStamp(langFile, versionId))
                .then(json => {
                    new Strings(json);
                    StringsFactory._initialized = true;
                    StringsFactory.currentLanguage = lang;
                    return lang;
                })
                .catch(err => {
                    if (StringsFactory.isFullLang(lang)) {
                        return _loadNeutral(err, lang)
                            .catch(err2 => {
                                return _loadDefault(err2);
                            });
                    }

                    return _loadDefault(err);
                })
                .finally(() => {
                    StringsFactory.loadComplete();
                });
        }

        const loadMetadata = Utils.isNullOrUndefined(StringsFactory._metadata);

        return loadMetadata
            ? FileUtils.readLocalJsonFile(UriHelper.setTimeStamp(metadataFile, versionId))
                .then((metadata: IStringsMetadata) => {
                    StringsFactory._metadata = metadata;
                    return _load();
                })
            : _load();
    }
}


/** WEBPACK FOOTER **
 ** ./Engine/Utils/StringsFactory.ts
 **/